import { z } from "zod";
import { VoiceSessionEndReason } from "./constants";

// Why a redemption was refused, one per error redeemAccessCode throws for a bad code.
export const AccessCodeRedemptionFailureReason = {
  NotFound: "not_found",
  Revoked: "revoked",
  Expired: "expired",
  LoginWindowExpired: "login_window_expired",
} as const;
export type AccessCodeRedemptionFailureReason =
  (typeof AccessCodeRedemptionFailureReason)[keyof typeof AccessCodeRedemptionFailureReason];

// The product analytics event catalogue. Every event the backend emits is named
// here, and every name is paired with a schema below — adding one without the
// other is a type error at the `track()` call site.
//
// What belongs here: things that leave no trace anywhere else in the schema (a
// buy link opened but never converted, an interview abandoned partway through).
// What doesn't: figures the relational tables already own. Completion rates come
// from Assessment.status and revenue from CreditTransaction — reporting those
// from the event log instead would give us two answers to the same question,
// and the log is the less trustworthy of the two.
export const AnalyticsEventName = {
  UserSignedUp: "user_signed_up",
  AccessCodeIssued: "access_code_issued",
  AccessCodeRedeemed: "access_code_redeemed",
  AssessmentCreated: "assessment_created",
  AssessmentCompleted: "assessment_completed",
  PackagePurchased: "package_purchased",
  HtsSubmissionFinalized: "hts_submission_finalized",
  AccessCodeRedemptionFailed: "access_code_redemption_failed",
  CheckoutStarted: "checkout_started",
  VoiceSessionEnded: "voice_session_ended",
} as const;
// Deliberately absent: a credits_spent event. Every spend is already a
// CreditTransaction row carrying the amount, the user and the assessment or HTS
// submission it paid for, and each spend is implied by the event that caused it
// (AssessmentCreated, or an HTS start/retake). Emitting it as well would mean
// instrumenting three call sites that all run inside someone else's
// transaction, to record something the schema answers exactly.
export type AnalyticsEventName = (typeof AnalyticsEventName)[keyof typeof AnalyticsEventName];

// Properties never carry candidate free-form input. HTS answers are encrypted at
// rest (see htsPayloadCipher.ts); routing them through analytics as plain
// properties would undo that. Ids, counts, enums and booleans only.
export const AnalyticsEventProperties = {
  [AnalyticsEventName.UserSignedUp]: z.object({
    isGuest: z.boolean(),
  }),

  // A code was minted. The package is named here, not just referenced, because
  // a redeemed code outlives its package (see `AccessCode.packageId`): once the
  // package is deleted, this event is the only place its name still sits next
  // to the code it granted.
  [AnalyticsEventName.AccessCodeIssued]: z.object({
    accessCodeId: z.string(),
    packageId: z.string(),
    packageName: z.string(),
    // Admin-entered cohort labels, the same ones carried as a guest trait on
    // redemption — nothing a candidate typed.
    tags: z.array(z.string()),
    expiresInHours: z.number().int().positive(),
  }),

  [AnalyticsEventName.AccessCodeRedeemed]: z.object({
    accessCodeId: z.string(),
    packageId: z.string(),
  }),

  [AnalyticsEventName.AssessmentCreated]: z.object({
    assessmentId: z.string(),
    interviewTemplateId: z.string(),
    // A retake re-does a subset of the template rather than the whole thing.
    isRetake: z.boolean(),
    retakeItemCount: z.number().int().nonnegative(),
  }),

  [AnalyticsEventName.AssessmentCompleted]: z.object({
    assessmentId: z.string(),
    interviewTemplateId: z.string(),
    // Null when no response carried a score — a sitting can finish without one.
    totalScore: z.number().nullable(),
    responseCount: z.number().int().nonnegative(),
  }),

  [AnalyticsEventName.PackagePurchased]: z.object({
    packageId: z.string(),
    // Stripe's own id, so a row here can be reconciled against the dashboard.
    checkoutSessionId: z.string(),
  }),

  [AnalyticsEventName.HtsSubmissionFinalized]: z.object({
    submissionId: z.string(),
    // HtsSubmission links to the template, not to the questionnaire — the
    // questionnaire is reached through the template's QuestionSet.
    interviewTemplateId: z.string(),
  }),
  // The only record of a failed redemption. Never includes the typed code:
  // it's user input, and may be a near-miss of a real one.
  [AnalyticsEventName.AccessCodeRedemptionFailed]: z.object({
    reason: z.enum(AccessCodeRedemptionFailureReason),
    // Absent when the code doesn't exist.
    accessCodeId: z.string().optional(),
  }),

  // A buy link opened a Stripe checkout. Joined to PackagePurchased on
  // checkoutSessionId, it gives the abandoned-checkout rate.
  [AnalyticsEventName.CheckoutStarted]: z.object({
    packageId: z.string(),
    checkoutSessionId: z.string(),
  }),

  // Voice calls aren't stored anywhere else. One event at the end covers what
  // a "started" event would.
  [AnalyticsEventName.VoiceSessionEnded]: z.object({
    assessmentId: z.string(),
    endReason: z.enum(VoiceSessionEndReason),
    durationSeconds: z.number().int().nonnegative(),
  }),
} as const satisfies Record<AnalyticsEventName, z.ZodType>;

// Maps an event name to the properties that event requires, so `track()` can be
// generic over the name and reject a mismatched payload at compile time.
export type AnalyticsEventPropertiesFor<TName extends AnalyticsEventName> =
  z.infer<(typeof AnalyticsEventProperties)[TName]>;

// Traits attached to the person record rather than to a single event.
export const AnalyticsUserTraitsSchema = z.object({
  email: z.string().optional(),
  // PostHog labels a person by email, then name, and ignores first/last name.
  // Guests have no real email, so this is what labels them.
  name: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  role: z.string().optional(),
  isGuest: z.boolean(),
  // A guest has exactly one access code for life, so it lives on the person.
  // Set at redemption; any event can then be filtered by it.
  accessCodeId: z.string().optional(),
  accessCodeTags: z.array(z.string()).optional(),
  packageId: z.string().optional(),
});
export type AnalyticsUserTraits = z.infer<typeof AnalyticsUserTraitsSchema>;

// Keeps these ids from colliding with other ids and makes them recognisable.
const DISTINCT_ID_PREFIX = "user_";

// The one place a user becomes an analytics identity, shared so the browser and
// API agree on it. Uses the database id because it's the only one that never
// changes: a Clerk id starts as an invitation id and is swapped at first login,
// and guests have synthetic ones.
export const resolveDistinctId = (user: { id: number }): string =>
  `${DISTINCT_ID_PREFIX}${user.id}`;
